/**
 * Drive-segment validation shared by the check-in flow (#1991 / diag #34).
 *
 * The CallLog serializer rejects a check-in whose drive segment has
 * `stdrivetime >= eddrivetime` ("Start and End Drive time not selected
 * properly"). A retroactive Start Day (or a re-used/stale closed-drive
 * entry) can occasionally produce a zero-length or inverted segment.
 * Rather than send it and get a 400 that blocks the rep's check-in, we
 * drop the segment — the call time + odometers still record the visit.
 *
 * The comparison is made at SECOND precision because that is what the wire
 * carries: a drive under a second is not inverted in memory but serializes to
 * two identical timestamps, which the server rejects. That happens whenever a
 * rep checks in immediately after clocking in — clocking in at the store, or
 * checking in the moment they arrive.
 */

import { MAX_PLAUSIBLE_DRIVE_MINUTES, isImplausibleDriveSpan, odometerMiles } from './dayLegs';
import { sameRecordedMinute } from './visitTime';

/**
 * @deprecated The send rule is no longer a flat duration — see
 * {@link isImplausibleDriveSpan}. Kept as the duration at which a drive starts
 * being questioned, for callers that still name it.
 */
export const MAX_DRIVE_MINUTES = MAX_PLAUSIBLE_DRIVE_MINUTES;

export interface DriveLike {
  startTime: Date;
  endTime: Date;
  startOdometer?: number;
  endOdometer?: number;
  durationMinutes?: number;
}

/**
 * Returns a send-safe drive segment, or undefined when there's nothing
 * sendable:
 *   - undefined for null/undefined, malformed times, or a zero-length /
 *     inverted time interval (server rejects stdrivetime >= eddrivetime).
 *   - a lone odometer reading is cleared (the serializer requires the pair
 *     together) while the time segment is kept. An equal pair is a valid
 *     zero-mile drive and is sent as-is (#3618).
 */
export function sanitizeDriveData<T extends DriveLike>(data: T | null | undefined): T | undefined {
  if (!data) return undefined;
  const { startTime, endTime } = data;
  if (!(startTime instanceof Date) || !(endTime instanceof Date)) return undefined;
  const start = startTime.getTime();
  const end = endTime.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined;
  // Compare the MINUTES this will be recorded as (#3452). A drive that rounds
  // to a single minute is not a drive — it reaches the server as
  // stdrivetime == eddrivetime and is rejected outright, killing the check-in.
  // Stating the rule in the domain's own unit also stops it depending on where
  // the clock happens to tick.
  if (start >= end || sameRecordedMinute(startTime, endTime)) return undefined;
  // A drive nobody could have made is a punch the rep forgot to close, not
  // hours of driving. Sending it would put that span on a real CallLog and bill
  // it; the leg stays for them to correct on the Time page. Same rule the day
  // totals use, so what the rep sees and what the server records cannot
  // disagree.
  const miles = odometerMiles(data.startOdometer, data.endOdometer);
  if (isImplausibleDriveSpan(Math.floor((end - start) / 60_000), miles)) return undefined;

  const cleared = clearLoneOdometer(data.startOdometer, data.endOdometer);
  if (cleared.startOdometer !== data.startOdometer || cleared.endOdometer !== data.endOdometer) {
    return { ...data, ...cleared };
  }
  return data;
}

/**
 * The serializer requires the pair together ("Both Start and End miles must
 * be entered together"), so a lone reading is a 400 that kills the check-in.
 * A leg always opens with a start reading but may close without an end one,
 * so half-pairs are routine. A complete pair — equal included — passes
 * through untouched (#3618).
 */
export function clearLoneOdometer(
  startOdometer: number | undefined,
  endOdometer: number | undefined,
): { startOdometer: number | undefined; endOdometer: number | undefined } {
  if ((startOdometer == null) !== (endOdometer == null)) {
    return { startOdometer: undefined, endOdometer: undefined };
  }
  return { startOdometer, endOdometer };
}
